import { merge } from '@/helpers/Utility';
import { Fragment } from 'react';
import { DateMatrixTableProps } from './dateMatrix.types';
import { getDateMatrixContentMinWidth } from './dateMatrix.utils';

const DEFAULT_LEFT_COLUMN_WIDTH = 220;
const DEFAULT_DAY_COLUMN_MIN_WIDTH = 92;
const DEFAULT_SUMMARY_COLUMN_WIDTH = 120;

const defaultDateHeader = (date: Date) => {
  return (
    <div className="border-b border-r border-primary-std px-2 py-2 text-center text-xs font-semibold text-secondary-std">
      <div>{date.toLocaleDateString('en-GB', { weekday: 'narrow', timeZone: 'UTC' })}</div>
      <div className="text-sm text-primary-std">{date.getUTCDate()}</div>
    </div>
  );
};

export const DateMatrixTable = <TRow,>({
  dates,
  rows,
  rowKey,
  leftHeader,
  summaryHeader,
  renderRowHeader,
  renderCell,
  renderSummaryCell,
  renderDateHeader,
  onDateHeaderClick,
  className,
  rowHeightClassName,
  leftColumnWidth = DEFAULT_LEFT_COLUMN_WIDTH,
  dayColumnMinWidth = DEFAULT_DAY_COLUMN_MIN_WIDTH,
  summaryColumnWidth = DEFAULT_SUMMARY_COLUMN_WIDTH
}: DateMatrixTableProps<TRow>) => {
  const hasSummaryColumn = Boolean(summaryHeader || renderSummaryCell);
  const contentMinWidth = getDateMatrixContentMinWidth({
    leftColumnWidth,
    dateColumnCount: dates.length,
    dayColumnWidth: dayColumnMinWidth,
    summaryColumnWidth,
    hasSummaryColumn
  });
  const gridTemplateColumns = `${leftColumnWidth}px repeat(${dates.length}, minmax(${dayColumnMinWidth}px, 1fr))${
    hasSummaryColumn ? ` ${summaryColumnWidth}px` : ''
  }`;

  return (
    <div className={merge('overflow-auto', className)}>
      <div className="w-full pr-4 border-r" style={{ minWidth: `${contentMinWidth}px` }}>
        <div className="bg-primary-std">
          <div className="grid w-full" style={{ gridTemplateColumns }}>
            <div className="sticky left-0 top-0 z-20 min-w-0 border-b border-r border-primary-std bg-primary-std px-4 py-3 text-sm font-semibold text-primary-std">
              {leftHeader}
            </div>
            {dates.map(date => {
              const headerContent = renderDateHeader ? renderDateHeader(date) : defaultDateHeader(date);

              if (!onDateHeaderClick) {
                return (
                  <div key={date.toISOString()} className="sticky top-0 z-10 bg-primary-std min-w-0">
                    {headerContent}
                  </div>
                );
              }

              return (
                <button
                  key={date.toISOString()}
                  type="button"
                  onClick={() => onDateHeaderClick(date)}
                  className="sticky top-0 min-w-0 z-10 text-left transition hover:bg-secondary-std"
                >
                  {headerContent}
                </button>
              );
            })}
            {hasSummaryColumn && (
              <div className="sticky top-0 z-20 min-w-0 border-b border-r border-primary-std bg-primary-std px-4 py-3 text-sm font-semibold text-primary-std">
                {summaryHeader}
              </div>
            )}

            {rows.map(row => (
              <Fragment key={rowKey(row)}>
                <div
                  className={merge(
                    'sticky left-0 z-10 min-w-0 border-b border-r border-primary-std bg-primary-std',
                    rowHeightClassName
                  )}
                >
                  {renderRowHeader(row)}
                </div>
                {dates.map(date => (
                  <div
                    key={`${rowKey(row)}-${date.toISOString()}`}
                    className={merge('min-w-0 border-b border-r border-primary-std', rowHeightClassName)}
                  >
                    {renderCell(row, date)}
                  </div>
                ))}
                {hasSummaryColumn && (
                  <div className="min-w-0 border-b border-r border-primary-std bg-primary-std">
                    {renderSummaryCell ? renderSummaryCell(row) : null}
                  </div>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
